import React from "react";
import { IconChevronDown } from "@tabler/icons-react";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50 disabled:pointer-events-none";

const btnVariants = {
  primary: "bg-brand text-white hover:bg-brand-strong",
  secondary: "bg-white text-black hover:bg-white/90",
  danger: "bg-red-600 text-white hover:bg-red-700",
  ghost: "bg-transparent text-white/70 hover:bg-white/10 hover:text-white",
};

export function Button({ variant = "primary", type = "button", className = "", children, ...props }) {
  return (
    <button
      type={type}
      className={`${btnBase} ${btnVariants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

const badgeTones = {
  neutral: "bg-white/10 text-white/60",
  active: "bg-brand text-white",
  inactive: "bg-white/10 text-white/60",
  suspended: "bg-red-600 text-white",
  warning: "bg-amber-400 text-black",
  success: "bg-emerald-500 text-black",
};

export function Badge({ tone = "neutral", className = "", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${badgeTones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm font-normal text-white placeholder-gray-500 outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand";

export function Input({ label, className = "", ...props }) {
  if (label) {
    return (
      <label className="block">
        <span className="mb-1.5 block text-sm font-semibold text-white/70">{label}</span>
        <input className={`${fieldClass} ${className}`} {...props} />
      </label>
    );
  }
  return <input className={`${fieldClass} ${className}`} {...props} />;
}

export function Select({ label, className = "", children, ...props }) {
  const select = (
    <div className="relative">
      <select
        className={`${fieldClass} appearance-none pr-9 ${className}`}
        {...props}
      >
        {children}
      </select>
      <IconChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
        stroke={1.75}
      />
    </div>
  );
  if (label) {
    return (
      <label className="block">
        <span className="mb-1.5 block text-sm font-semibold text-white/70">{label}</span>
        {select}
      </label>
    );
  }
  return select;
}

export function Textarea({ label, className = "", ...props }) {
  if (label) {
    return (
      <label className="block">
        <span className="mb-1.5 block text-sm font-semibold text-white/70">{label}</span>
        <textarea className={`${fieldClass} ${className}`} {...props} />
      </label>
    );
  }
  return <textarea className={`${fieldClass} ${className}`} {...props} />;
}

export function Spinner({ className = "h-4 w-4" }) {
  return (
    <svg className={`animate-spin text-current ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

export function EmptyState({ title = "Sin datos", description, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/40">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25-4v3m4.5-3v3M8.25 7.5h7.5"
          />
        </svg>
      </div>
      <div>
        <p className="text-sm font-medium text-white">{title}</p>
        {description && <p className="mt-1 text-xs text-white/50">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-xs text-red-400">{message}</p>;
}